import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import RoleGuard from "../components/RoleGuard";
import NavBar from "../app/components/NavBar";
import { getRoleDisplayName, getRoleBadgeColor } from "../utils/roleUtils";
import api from "../app/api";
import { allowedEmailAPI } from "../utils/allowedEmailAPI";

const AdminManagementPage = () => {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailInput, setEmailInput] = useState("");
  const [allowedEmails, setAllowedEmails] = useState([]);
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [permissionForm, setPermissionForm] = useState([]);
  const user = useSelector((state) => state.auth.user);
  const navigate = useNavigate();

  // Backend-enforced permissions list (must match Admin.js schema)
  const ALL_PERMISSIONS = [
    "leads_read",
    "leads_write",
    "leads_delete",
    "admins_read",
    "admins_write",
    "admins_delete",
    "manage_admins",
    "settings_read",
    "settings_write",
  ];

  const fetchAdmins = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.getAdminsList();
      setAdmins(response.data);
    } catch (error) {
      toast.error(error.message || "Failed to fetch admins");
      console.error("Error:", error);

      // If session related error, redirect to login
      if (
        error.message &&
        (error.message.includes("No session ID provided") ||
          error.message.includes("Invalid or expired session") ||
          error.message.includes("Session expired"))
      ) {
        navigate("/login");
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchAdmins();
  }, [fetchAdmins]);

  const handleToggleStatus = async (adminId, currentStatus) => {
    try {
      const response = await api.toggleAdminStatus(adminId, !currentStatus);
      toast.success(response.message || "Admin status updated successfully");
      fetchAdmins(); // Refresh the list
    } catch (error) {
      toast.error(error.message || "Failed to update admin status");
      console.error("Error:", error);
    }
  };

  const handlePromoteToSuperAdmin = async (adminId, adminName) => {
    // Confirm the action
    if (
      !window.confirm(
        `Are you sure you want to promote ${adminName} to Super Admin? This will give them full administrative privileges.`
      )
    ) {
      return;
    }

    try {
      const response = await api.promoteToSuperAdmin(adminId);
      toast.success(
        response.message || "Admin promoted to Super Admin successfully"
      );
      fetchAdmins(); // Refresh the list
    } catch (error) {
      toast.error(error.message || "Failed to promote admin to Super Admin");
      console.error("Error:", error);
    }
  };

  const handleDemoteToAdmin = async (adminId, adminName) => {
    // Confirm the action
    if (
      !window.confirm(
        `Are you sure you want to demote ${adminName} from Super Admin to regular Admin? This will remove their super admin privileges.`
      )
    ) {
      return;
    }

    try {
      const response = await api.demoteToAdmin(adminId);
      toast.success(
        response.message || "Super Admin demoted to regular Admin successfully"
      );
      fetchAdmins(); // Refresh the list
    } catch (error) {
      toast.error(
        error.message || "Failed to demote Super Admin to regular Admin"
      );
      console.error("Error:", error);
    }
  };

  const fetchAllowedEmails = async () => {
    try {
      const response = await allowedEmailAPI.getAll(1, 100);
      setAllowedEmails(response.data);
    } catch (error) {
      toast.error(error.message || "Failed to fetch allowed emails");
      console.error("Error:", error);

      // If session related error, redirect to login
      if (
        error.message &&
        (error.message.includes("No session ID provided") ||
          error.message.includes("Invalid or expired session") ||
          error.message.includes("Session expired"))
      ) {
        navigate("/login");
      }
    }
  };

  const handleEmailInputChange = (e) => {
    setEmailInput(e.target.value);
  };

  const handleEmailKeyDown = (e) => {
    // Check for comma or enter key
    if (e.key === "," || e.key === "Enter") {
      e.preventDefault();
      // Add the current email to the list if it's valid
      addEmail();
    } else if (
      e.key === "Backspace" &&
      emailInput === "" &&
      allowedEmails.length > 0
    ) {
      // If Backspace is pressed and the input is empty, remove the last email
      setAllowedEmails(allowedEmails.slice(0, -1));
    }
  };

  const addEmail = async () => {
    const email = emailInput.trim();
    if (!email) return;

    // Simple email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    try {
      await allowedEmailAPI.add(email);
      await fetchAllowedEmails();
      setEmailInput("");
      toast.success(`Email ${email} added successfully`);
    } catch (error) {
      toast.error(error.message || "Failed to add email");
    }
  };

  const handleOpenPermissionModal = (admin) => {
    setSelectedAdmin(admin);
    setPermissionForm(admin.permissions || []);
    setShowPermissionModal(true);
  };

  const handlePermissionChange = (perm) => {
    setPermissionForm((prev) =>
      prev.includes(perm) ? prev.filter((p) => p !== perm) : [...prev, perm]
    );
  };

  const handleSavePermissions = async () => {
    try {
      await api.updateAdminPermissions(selectedAdmin._id, permissionForm);
      toast.success("Permissions updated successfully");
      setShowPermissionModal(false);
      fetchAdmins(); // Refresh the list
    } catch (error) {
      toast.error(error.message || "Failed to update permissions");
      console.error("Error:", error);
    }
  };

  const handleFixPermissions = async () => {
    try {
      const response = await api.fixSuperAdminPermissions();
      toast.success(response.message || "Permissions fixed successfully");
      fetchAdmins(); // Refresh the list
    } catch (error) {
      toast.error(error.message || "Failed to fix permissions");
      console.error("Error:", error);
    }
  };

  const removeAllowedEmail = async (emailId) => {
    try {
      await allowedEmailAPI.remove(emailId);
      await fetchAllowedEmails();
      toast.success("Email removed successfully");
    } catch (error) {
      toast.error(error.message || "Failed to remove email");
    }
  };

  return (
    <RoleGuard
      allowedRoles={["super_admin"]}
      requiredPermissions={["manage_admins"]}
    >
      <div className="min-h-screen bg-gray-100">
        <NavBar />
        <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <div className="sm:flex sm:items-center sm:justify-between">
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">
                    Admin Management
                  </h1>
                  <p className="mt-2 text-sm text-gray-700">
                    Manage administrators and their permissions.
                  </p>
                </div>
                <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none space-x-2">
                  <button
                    onClick={handleFixPermissions}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    title="Fix missing permissions for existing super admins"
                  >
                    <svg
                      className="mr-2 h-4 w-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                    Fix Permissions
                  </button>
                  <button
                    onClick={() => {
                      fetchAllowedEmails();
                      setShowEmailModal(true);
                    }}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    <svg
                      className="mr-2 h-4 w-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                      />
                    </svg>
                    Allowed Emails
                  </button>
                </div>
              </div>

              {loading ? (
                <div className="flex justify-center mt-8">
                  <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-500"></div>
                </div>
              ) : (
                <div className="mt-8 flex flex-col">
                  <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
                    <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
                      <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                        <table className="min-w-full divide-y divide-gray-300">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Admin
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Role
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Status
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Last Login
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {admins.map((admin) => (
                              <tr key={admin._id}>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="flex items-center">
                                    <div className="flex-shrink-0 h-10 w-10">
                                      <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                                        <span className="text-sm font-medium text-gray-700">
                                          {admin.name.charAt(0).toUpperCase()}
                                        </span>
                                      </div>
                                    </div>
                                    <div className="ml-4">
                                      <div className="text-sm font-medium text-gray-900">
                                        {admin.name}
                                      </div>
                                      <div className="text-sm text-gray-500">
                                        {admin.email}
                                      </div>
                                    </div>
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span
                                    className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRoleBadgeColor(
                                      admin.role
                                    )}`}
                                  >
                                    {getRoleDisplayName(admin.role)}
                                  </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span
                                    className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                      admin.isActive
                                        ? "bg-green-100 text-green-800"
                                        : "bg-red-100 text-red-800"
                                    }`}
                                  >
                                    {admin.isActive ? "Active" : "Inactive"}
                                  </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {admin.lastLogin
                                    ? new Date(
                                        admin.lastLogin
                                      ).toLocaleDateString()
                                    : "Never"}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                  {admin._id !== user.id && (
                                    <div className="flex space-x-2">
                                      {admin.role === "super_admin" ? (
                                        // For super admins, only show demote button (visible to current super admin only)
                                        user.role === "super_admin" && (
                                          <button
                                            onClick={() =>
                                              handleDemoteToAdmin(
                                                admin._id,
                                                admin.name
                                              )
                                            }
                                            className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
                                          >
                                            <svg
                                              className="mr-1 h-4 w-4"
                                              fill="none"
                                              stroke="currentColor"
                                              viewBox="0 0 24 24"
                                            >
                                              <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6"
                                              />
                                            </svg>
                                            Demote to Admin
                                          </button>
                                        )
                                      ) : (
                                        // For regular admins, show all management buttons
                                        <>
                                          <button
                                            onClick={() =>
                                              handleOpenPermissionModal(admin)
                                            }
                                            className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-yellow-500 hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-400"
                                          >
                                            Manage Permissions
                                          </button>

                                          {user.role === "super_admin" && (
                                            <button
                                              onClick={() =>
                                                handlePromoteToSuperAdmin(
                                                  admin._id,
                                                  admin.name
                                                )
                                              }
                                              className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                                            >
                                              <svg
                                                className="mr-1 h-4 w-4"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                              >
                                                <path
                                                  strokeLinecap="round"
                                                  strokeLinejoin="round"
                                                  strokeWidth={2}
                                                  d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                                                />
                                              </svg>
                                              Promote to Super Admin
                                            </button>
                                          )}

                                          <button
                                            onClick={() => {
                                              if (
                                                user.role !== "super_admin" &&
                                                !user.permissions?.includes(
                                                  "manage_admins"
                                                )
                                              ) {
                                                toast.error(
                                                  "You do not have permission to manage admins."
                                                );
                                                return;
                                              }
                                              handleToggleStatus(
                                                admin._id,
                                                admin.isActive
                                              );
                                            }}
                                            className={`inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md text-white ${
                                              admin.isActive
                                                ? "bg-red-600 hover:bg-red-700"
                                                : "bg-green-600 hover:bg-green-700"
                                            } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500`}
                                          >
                                            {admin.isActive
                                              ? "Deactivate"
                                              : "Activate"}
                                          </button>
                                        </>
                                      )}
                                    </div>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Email Management Modal */}
          {showEmailModal && (
            <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
              <div className="relative top-20 mx-auto p-5 border w-[600px] shadow-lg rounded-md bg-white">
                <div className="mt-3">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    Allowed Email Management
                  </h3>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Add Email (Press Enter or Comma to add)
                    </label>
                    <div className="flex items-center">
                      <input
                        type="text"
                        value={emailInput}
                        onChange={handleEmailInputChange}
                        onKeyDown={handleEmailKeyDown}
                        placeholder="Enter email address"
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <button
                        onClick={addEmail}
                        className="ml-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        Add
                      </button>
                    </div>
                  </div>

                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">
                      Allowed Emails ({allowedEmails.length})
                    </h4>
                    <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-md">
                      {allowedEmails.length === 0 ? (
                        <p className="text-sm text-gray-500 p-4">
                          No allowed emails configured
                        </p>
                      ) : (
                        allowedEmails.map((email) => (
                          <div
                            key={email._id}
                            className="flex items-center justify-between p-2 border-b border-gray-200 last:border-b-0"
                          >
                            <div>
                              <span className="text-sm font-medium text-gray-900">
                                {email.email}
                              </span>
                              <div className="text-xs text-gray-500">
                                {email.isUsed ? (
                                  <span className="text-green-600">
                                    Used by {email.usedBy?.name || "Unknown"}
                                  </span>
                                ) : (
                                  <span className="text-gray-600">
                                    Available
                                  </span>
                                )}
                              </div>
                            </div>
                            <button
                              onClick={() => removeAllowedEmail(email._id)}
                              className="text-red-600 hover:text-red-800 text-sm"
                            >
                              Remove
                            </button>
                          </div>
                        ))
                      )}
                    </div>
                  </div>

                  <div className="flex justify-end space-x-2">
                    <button
                      onClick={() => setShowEmailModal(false)}
                      className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Permission Management Modal */}
          {showPermissionModal && selectedAdmin && (
            <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
              <div className="relative top-20 mx-auto p-5 border w-[500px] shadow-lg rounded-md bg-white">
                <div className="mt-3">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    Manage Permissions for {selectedAdmin.name}
                  </h3>

                  <div className="mb-4">
                    <div className="grid grid-cols-1 gap-2">
                      {ALL_PERMISSIONS.map((permission) => (
                        <label
                          key={permission}
                          className="flex items-center space-x-3"
                        >
                          <input
                            type="checkbox"
                            checked={permissionForm.includes(permission)}
                            onChange={() => handlePermissionChange(permission)}
                            className="form-checkbox h-4 w-4 text-blue-600"
                          />
                          <span className="text-sm text-gray-700">
                            {permission.replace(/_/g, " ").toUpperCase()}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="flex justify-end space-x-2">
                    <button
                      onClick={() => setShowPermissionModal(false)}
                      className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSavePermissions}
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      Save Permissions
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </RoleGuard>
  );
};

export default AdminManagementPage;
